import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { cloudSaveNames } from "./emulator/cloudSaves";
import { settleProfile } from "./emulator/profiles";
import { reloadWhenSessionEnds, session, takeSignInFromUrl } from "./auth/session";
import { settlePreferences } from "@common/preferences";
import { loadControllers } from "./emulator/controllers";
import { recordVisit } from "@jkast90/kast-common/kast-dev-api";
import { getDeploymentConfig } from "@common/deployment-config";
import { followAccountTheme } from "@common/theme";
import { registerServiceWorker } from "@jkast90/kast-common/offline";
import { isDesktop } from "./desktop/desktop";
// The shared base first: it declares the cascade layers (reset, kt, kui) and
// the reset. Then the family palettes, the scale tokens, the shared UI and
// the app's own sheet.
import "@jkast90/kast-common/base.css";
import "@jkast90/kast-common/theme.css";
import "@jkast90/kast-common/tokens.css";
import "@jkast90/kast-common/ui.css";
import "./index.css";

const container = document.getElementById("root")!;
// Counts this page load in the central visitor log (api/src/visitors.rs):
// someone playing without signing in calls nothing else.
recordVisit(getDeploymentConfig().apiUrl);
// So the next load opens with no network (the worker vite.config.ts builds).
// The desktop app loads the UI from its own bundle and needs none; dev
// removes any a preview left, or it would serve cached files.
registerServiceWorker({ enabled: import.meta.env.PROD && !isDesktop });
// index.html already set the theme; the store follows the OS for "system"
// and other tabs, and this takes the account's choice once
// settlePreferences brings it.
followAccountTheme();

// Back from sign-in: the tokens go into the session and out of the URL
// first. Then, before anything reads the browser's saves and settings, make
// them the signed-in account's: settleProfile claims the browser for the
// account that just signed in, or adopts what was played signed out.
async function start() {
  // Settled first: a stored session that had expired is refreshed (or
  // ended) before anyone's saves move or any call goes out on it.
  await session.ready();
  const sub = session.getState().user?.sub || null;
  // From here a session that ends by itself reloads, as a sign-out does.
  reloadWhenSessionEnds();
  await settleProfile(
    sub,
    () => {
      container.textContent = "Switching to this account's saves…";
    },
    {
      // Another tab can sign in as someone else meanwhile (the session
      // follows it): then this account's slots can't be listed, so the
      // adoption waits for the next load.
      cloudNames: async () => {
        const token = session.bearer();
        return token && session.getState().user?.sub === sub ? cloudSaveNames(token) : null;
      },
    },
  );
  await settlePreferences();
  // The shared controller profiles, read again in the background; a game
  // that opens first plays with the cached copy and rebinds when it lands.
  void loadControllers();
  ReactDOM.createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Sign-in started on the player goes back there by a full page load.
if (!takeSignInFromUrl()) void start();
